import React, { useState } from "react";
import DatabaseHome from "./DatabaseHome";
import IngredientsListScreen from "./IngredientsListScreen";
import FormulaListScreen from "./FormulaListScreen";
import IngredientDetailScreen from "./IngredientDetailScreen";
import IngredientCreateScreen from "./IngredientCreateScreen";
import IngredientEditScreen from "./IngredientEditScreen";
import FormulaSheet from "./FormulaSheet";
import { supabase } from "./supabase/auth";
import { useTranslation } from 'react-i18next';

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

type ActiveView = "home" | "ingredients" | "formulas" | "ingredient-detail" | "ingredient-create" | "ingredient-edit" | "formula-detail" | "formula-create";

export default function DatabaseModule({
  initialView = "home",
}: {
  initialView?: "home" | "ingredients" | "formulas";
}) {
  const { t } = useTranslation();

  const [activeView, setActiveView] = useState<ActiveView>(initialView);
  const [selectedIngredientId, setSelectedIngredientId] = useState<string>("");
  const [selectedFormulaCode, setSelectedFormulaCode] = useState<string>("");

  const handleNav = (id: string) => {
    if (id === "home" || id === "ingredients" || id === "formulas") {
      setActiveView(id);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "calc(100vh - 96px)",
        fontFamily: FONT_STACK,
        background: "transparent",
        color: "#f8fafc",
      }}
    >
      {/* ── Main Content (no internal sidebar — unified nav handles top-level navigation;
              page background is the global wavy from Root.tsx) ── */}
      <div style={{ marginLeft: 0, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Top bar — glassy dark to match the rest of the app */}
        <div
          style={{
            padding: "12px 24px",
            borderBottom: "1px solid rgba(139,92,246,0.22)",
            background: "rgba(15,11,42,0.4)",
            backdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "3px",
              textTransform: "uppercase",
              color: "#c4b5fd",
            }}
          >
            {t('database.database')}
          </h1>
        </div>

        {/* Content area — transparent so the global wavy backdrop shows through */}
        <div style={{ flex: 1, padding: "16px 24px", overflowY: "auto", background: "transparent" }}>
          {activeView === "home" && <DatabaseHome onNavigate={(v) => handleNav(v)} />}
          {activeView === "ingredients" && (
            <IngredientsListScreen
              onSelectIngredient={(id) => { setSelectedIngredientId(id); setActiveView("ingredient-detail"); }}
              onAddIngredient={() => setActiveView("ingredient-create")}
            />
          )}
          {activeView === "formulas" && (
            <FormulaListScreen
              onSelectFormula={(code) => { setSelectedFormulaCode(code); setActiveView("formula-detail"); }}
              onAddFormula={() => setActiveView("formula-create")}
            />
          )}
          {activeView === "formula-detail" && selectedFormulaCode && (
            <FormulaSheet
              formulaCode={selectedFormulaCode}
              onBack={() => setActiveView("formulas")}
              onNavigateToFormula={(code) => { setSelectedFormulaCode(code); setActiveView("formula-detail"); }}
              supabase={supabase}
            />
          )}
          {activeView === "formula-create" && (
            <FormulaSheet
              formulaCode={null}
              onBack={() => setActiveView("formulas")}
              onNavigateToFormula={(code) => { setSelectedFormulaCode(code); setActiveView("formula-detail"); }}
              supabase={supabase}
            />
          )}
          {activeView === "ingredient-detail" && selectedIngredientId && (
            <IngredientDetailScreen
              ingredientId={selectedIngredientId}
              onBack={() => setActiveView("ingredients")}
              onEdit={(id) => { setSelectedIngredientId(id); setActiveView("ingredient-edit"); }}
            />
          )}
          {activeView === "ingredient-create" && (
            <IngredientCreateScreen
              onBack={() => setActiveView("ingredients")}
              onSaved={(id) => { setSelectedIngredientId(id); setActiveView("ingredient-detail"); }}
            />
          )}
          {activeView === "ingredient-edit" && selectedIngredientId && (
            <IngredientEditScreen
              ingredientId={selectedIngredientId}
              onBack={() => setActiveView("ingredients")}
              onSaved={(id) => { setSelectedIngredientId(id); setActiveView("ingredient-detail"); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
